import React from 'react';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';

import SkipLandingPage from './SkipLandingPage';

/**
 * Homepage metadata.
 *
 * This used to live as hardcoded tags in public/index.html, which meant every
 * page on the site served the homepage's canonical and description alongside
 * its own. The blog article was the visible casualty — it declared the homepage
 * as its canonical — so those tags moved out of the shared head.
 *
 * They land here rather than disappearing: SkipLandingPage is rendered directly
 * rather than through the <Page> component, so nothing else emits a title,
 * description or canonical for `/`. The values are carried over verbatim from
 * the old static head, so the homepage's metadata is unchanged in production —
 * it is simply page-specific now instead of global.
 */
const ROOT_URL = 'https://skipthedealerships.ai';
const HOME_TITLE = 'SkipTheDealerships.ai — Buy & Sell Cars Without the Dealership';
const HOME_DESCRIPTION =
  "Canada's AI-powered private vehicle marketplace. Buy or sell used cars, trucks, and SUVs " +
  'directly — no dealership markup, no middleman. KYC-verified buyers and sellers. Free listings.';
const HOME_SOCIAL_DESCRIPTION =
  "Canada's AI-powered private vehicle marketplace. Buy or sell used cars directly — no " +
  'dealership markup, no middleman. Free listings, KYC-verified.';
const HOME_IMAGE = `${ROOT_URL}/static/images/dealer-portal-preview.png`;

export const LandingPageComponent = props => {
  return (
    <>
      <Helmet>
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${ROOT_URL}/`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SkipTheDealerships.ai" />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_SOCIAL_DESCRIPTION} />
        <meta property="og:url" content={`${ROOT_URL}/`} />
        <meta property="og:image" content={HOME_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_CA" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={HOME_TITLE} />
        <meta
          name="twitter:description"
          content="Canada's AI-powered private vehicle marketplace. No dealership markup. Free listings. KYC-verified buyers and sellers."
        />
        <meta name="twitter:image" content={HOME_IMAGE} />
      </Helmet>
      <SkipLandingPage />
    </>
  );
};

const mapStateToProps = () => ({});
const mapDispatchToProps = () => ({});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
